import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api, formatPrice, logger } from "../lib/api";
import { ProductVisual } from "../components/ProductVisual";
import { FadeIn } from "../components/FadeIn";
import { toast } from "sonner";

const CTA_PRIMARY = "inline-block px-10 py-4 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.3em] font-semibold hover:bg-capra-amber transition-colors";
const CTA_GHOST = "inline-block px-10 py-4 border border-capra-ivory/30 text-capra-ivory text-[11px] uppercase tracking-[0.3em] hover:border-capra-bronze hover:text-capra-bronze transition-colors";

const HERO_ZOOM = { initial: { scale: 1.08 }, animate: { scale: 1 }, transition: { duration: 3.2, ease: [0.22, 1, 0.36, 1] } };

const TRACKSUIT_CAMPAIGN_LINES = ["Capra Falconeri", "TRACKSUITS", "HEAVYWEIGHT COLLECTION"];
const ACCESSORIES_CAMPAIGN_LINES = ["Accessories", "THE FINAL DETAIL."];

/* ---------- 1. HERO ---------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const scrollToDrop = () => document.getElementById("drop")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative h-screen min-h-[640px] overflow-hidden flex items-center justify-center" data-testid="hero-section">
      <motion.div
        className="absolute -inset-y-[8%] inset-x-0 monogram-hero-bg"
        style={{ y }}
        initial={HERO_ZOOM.initial}
        animate={HERO_ZOOM.animate}
        transition={HERO_ZOOM.transition}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-capra-night/30 via-transparent to-capra-night" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(10,7,6,0.55), transparent 72%)" }} />
      <div className="relative z-10 text-center px-6 animate-fade-up">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-capra-ivory tracking-[0.18em] sm:tracking-[0.26em] leading-tight">
          CAPRA
          <span className="block mt-2">FALCONERI</span>
        </h1>
        <p className="mt-6 text-[11px] uppercase tracking-[0.5em] text-capra-bronze">The First Collection</p>
        <p className="mt-8 font-display italic text-lg md:text-xl text-capra-beige/85">"Crafted for the few."</p>
        <button onClick={scrollToDrop} data-testid="hero-discover-drop-btn" className={`${CTA_PRIMARY} mt-12`}>
          Discover the Drop
        </button>
        <p className="mt-10 text-[10px] uppercase tracking-[0.45em] text-capra-muted/70">Limited Release · 2026</p>
      </div>
    </section>
  );
};

/* ---------- 2. BRAND STATEMENT ---------- */
const BrandStatement = () => (
  <section className="py-40 lg:py-64 px-6" data-testid="brand-statement-section">
    <FadeIn className="max-w-4xl mx-auto text-center">
      <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl text-capra-ivory tracking-[0.08em] leading-tight">
        NOT MADE<br />FOR EVERYONE.
      </h2>
      <p className="mt-14 text-sm text-capra-muted leading-loose max-w-md mx-auto tracking-wide">
        Capra Falconeri represents restraint, craftsmanship and distinction.
        Produced in limited quantities. Designed to remain rare.
      </p>
    </FadeIn>
  </section>
);

/* ---------- Campaign banner (shared by 01 / 02) ---------- */
const CampaignBanner = ({ id, number, lines, cta, to, image, position = "center", testId }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id={id} ref={ref} className="relative h-[92vh] min-h-[560px] overflow-hidden flex items-end" data-testid={testId}>
      <motion.div
        className="absolute -inset-y-[8%] inset-x-0 bg-cover"
        style={{ y, backgroundImage: `url('${image}')`, backgroundPosition: position }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-capra-night via-transparent to-capra-night" />
      <FadeIn className="relative z-10 px-6 lg:px-16 pb-20 lg:pb-28 max-w-[1600px] mx-auto w-full">
        <p className="font-display text-5xl lg:text-7xl text-capra-bronze/70">{number}</p>
        {lines.map((l, i) => (
          <p key={l} className={i === 0 ? "mt-4 text-[10px] uppercase tracking-[0.5em] text-capra-muted" : "font-display text-3xl sm:text-5xl lg:text-6xl text-capra-ivory tracking-[0.1em] mt-2"}>
            {l}
          </p>
        ))}
        <Link to={to} data-testid={`${testId}-cta`} className={`${CTA_GHOST} mt-10`}>
          {cta}
        </Link>
      </FadeIn>
    </section>
  );
};

/* ---------- Editorial product row ---------- */
const EditorialPiece = ({ product, index, flip }) => (
  <FadeIn>
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${flip ? "lg:[direction:rtl]" : ""}`} data-testid={`editorial-piece-${product.slug}`}>
      <Link to={`/product/${product.slug}`} className="group block overflow-hidden [direction:ltr]">
        <div className="aspect-[3/4] lg:h-[80vh] lg:aspect-auto transition-transform duration-[1.6s] ease-out group-hover:scale-[1.03]">
          <ProductVisual product={product} />
        </div>
      </Link>
      <div className="[direction:ltr] px-2 lg:px-0">
        <p className="text-[10px] uppercase tracking-[0.5em] text-capra-bronze">CF {String(index + 1).padStart(2, "0")}</p>
        <h3 className="font-display text-3xl lg:text-4xl text-capra-ivory mt-4 leading-tight">{product.name}</h3>
        <p className="mt-4 text-[10px] uppercase tracking-[0.35em] text-capra-muted">{product.materials.split("·")[0]}</p>
        <p className="mt-1 text-[10px] uppercase tracking-[0.35em] text-capra-muted">Limited Production</p>
        <p className="mt-6 font-display text-xl text-capra-gold">{formatPrice(product.price)}</p>
        <p className="mt-2 text-[10px] uppercase tracking-[0.35em] text-capra-ivory/40">Currently Sold Out</p>
        <Link to={`/product/${product.slug}`} data-testid={`view-piece-${product.slug}`} className={`${CTA_GHOST} mt-8`}>
          View Piece
        </Link>
      </div>
    </div>
  </FadeIn>
);

/* ---------- 4. EXCLUSIVITY ---------- */
const Exclusivity = () => (
  <section className="relative py-44 lg:py-64 px-6 leather-panel" data-testid="exclusivity-section">
    <FadeIn className="max-w-4xl mx-auto text-center">
      <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl text-capra-ivory tracking-[0.08em] leading-tight">
        NO RESTOCKS.<br />NO MASS PRODUCTION.
      </h2>
      <p className="mt-14 text-sm text-capra-muted leading-loose max-w-md mx-auto tracking-wide">
        Every Capra Falconeri release is produced in controlled quantities.
        Once a piece disappears, the collection moves forward.
      </p>
      <p className="mt-14 text-[11px] uppercase tracking-[0.5em] text-capra-bronze">Own the Release. Not the Trend.</p>
    </FadeIn>
  </section>
);

/* ---------- 6. SIGNATURE 001 ---------- */
const Signature = ({ product }) => {
  if (!product) return null;
  return (
    <section className="relative py-28 lg:py-40 px-6 lg:px-16" data-testid="signature-001-section">
      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-5 gap-12 items-center">
        <FadeIn className="lg:col-span-3">
          <Link to={`/product/${product.slug}`} className="group block overflow-hidden">
            <div className="aspect-[3/4] lg:h-[92vh] lg:aspect-auto transition-transform duration-[1.6s] ease-out group-hover:scale-[1.02]">
              <ProductVisual product={product} />
            </div>
          </Link>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.5em] text-capra-bronze">Capra Signature 001</p>
          <h2 className="font-display text-4xl lg:text-5xl text-capra-ivory mt-5 leading-tight">Only 100 pieces<br />produced.</h2>
          <div className="mt-10 space-y-3 text-[11px] uppercase tracking-[0.3em] text-capra-muted">
            <p>Italian-inspired construction</p>
            <p>Premium materials</p>
            <p>Limited release</p>
          </div>
          <p className="mt-8 font-display text-2xl text-capra-gold">{formatPrice(product.price)}</p>
          <Link to={`/product/${product.slug}`} data-testid="discover-001-cta" className={`${CTA_PRIMARY} mt-10`}>
            Discover 001
          </Link>
        </FadeIn>
      </div>
    </section>
  );
};

/* ---------- 7. ARCHIVE ---------- */
const Archive = ({ products }) => (
  <section className="py-32 lg:py-44 px-6 lg:px-16" data-testid="archive-section">
    <FadeIn className="text-center mb-20">
      <h2 className="font-display text-3xl lg:text-5xl text-capra-ivory tracking-[0.12em]">THE ARCHIVE</h2>
      <p className="mt-4 text-[10px] uppercase tracking-[0.4em] text-capra-muted">Pieces that will not return.</p>
    </FadeIn>
    <div className="max-w-[1500px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-16">
      {products.slice(0, 8).map((p, i) => (
        <FadeIn key={p.id} delay={(i % 4) * 0.08}>
          <div className="group" data-testid={`archive-piece-${p.slug}`}>
            <div className="aspect-[3/4] overflow-hidden grayscale-[0.7] brightness-[0.65] group-hover:grayscale-[0.4] group-hover:brightness-75 transition-all duration-1000">
              <ProductVisual product={p} emblemSize="medium" />
            </div>
            <p className="mt-5 font-display text-sm text-capra-ivory/60">{p.name}</p>
            <p className="mt-2 text-[9px] uppercase tracking-[0.45em] text-capra-muted/60">Sold Out</p>
          </div>
        </FadeIn>
      ))}
    </div>
  </section>
);

/* ---------- 8. BRAND WORLD ---------- */
const BrandWorld = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  return (
    <section ref={ref} className="relative h-[85vh] min-h-[520px] overflow-hidden flex items-center justify-center" data-testid="brand-world-section">
      <motion.div
        className="absolute -inset-y-[10%] inset-x-0 bg-cover bg-center"
        style={{ y, backgroundImage: "url('/brand/mountains.jpg')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-capra-night via-capra-night/30 to-capra-night" />
      <FadeIn className="relative z-10 text-center px-6">
        <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl text-capra-ivory tracking-[0.12em] leading-tight">
          BUILT ABOVE<br />THE ORDINARY.
        </h2>
        <p className="mt-8 text-[11px] uppercase tracking-[0.5em] text-capra-bronze">Capra Falconeri</p>
      </FadeIn>
    </section>
  );
};

/* ---------- 9. PRIVATE ACCESS ---------- */
const PrivateAccess = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Your request has been received. The maison will be in touch.");
      setEmail("");
    } catch (err) {
      logger.error("Private access request failed", err);
      toast.error("Please enter a valid email address.");
    }
    setBusy(false);
  };

  return (
    <section id="access" className="py-36 lg:py-52 px-6 monogram-veil" data-testid="private-access-section">
      <FadeIn className="max-w-xl mx-auto text-center">
        <h2 className="font-display text-3xl lg:text-4xl text-capra-ivory tracking-[0.15em]">PRIVATE ACCESS</h2>
        <p className="mt-6 text-sm text-capra-muted tracking-wide leading-relaxed">
          Receive access to future Capra Falconeri releases.
        </p>
        <form onSubmit={request} className="mt-12 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="private-access-form">
          <input
            data-testid="private-access-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="flex-1 bg-transparent border-b border-capra-bronze/40 px-2 py-3 text-sm text-capra-ivory text-center sm:text-left placeholder:text-capra-muted/50 outline-none focus:border-capra-bronze transition-colors"
          />
          <button data-testid="private-access-submit" disabled={busy} className={`${CTA_PRIMARY} disabled:opacity-60`}>
            Request Access
          </button>
        </form>
      </FadeIn>
    </section>
  );
};

/* ---------- PAGE ---------- */
export default function Home() {
  const [tracksuits, setTracksuits] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [archive, setArchive] = useState([]);
  const [signature, setSignature] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const { data } = await api.get("/products");
        setTracksuits(data.filter((p) => p.category === "tracksuits" && p.slug !== "capra-signature-001").slice(0, 3));
        setAccessories(data.filter((p) => p.category === "accessories").slice(0, 3));
        setArchive(data.filter((p) => ["men", "women", "leather-goods"].includes(p.category)));
        setSignature(data.find((p) => p.slug === "capra-signature-001") || null);
      } catch (err) {
        logger.error("Home fetch failed", err);
      }
    };
    fetchAll();
  }, []);

  return (
    <div data-testid="home-page" className="bg-capra-night">
      <Hero />
      <BrandStatement />

      <CampaignBanner
        id="drop"
        number="01"
        lines={TRACKSUIT_CAMPAIGN_LINES}
        cta="Explore Tracksuits"
        to="/tracksuits"
        image="/brand/cat-tracksuit.jpg"
        position="center 25%"
        testId="tracksuits-campaign"
      />
      <div className="px-6 lg:px-16 max-w-[1500px] mx-auto py-28 lg:py-40 space-y-32 lg:space-y-48">
        {tracksuits.map((p, i) => (
          <EditorialPiece key={p.id} product={p} index={i} flip={i % 2 === 1} />
        ))}
      </div>

      <Exclusivity />

      <CampaignBanner
        number="02"
        lines={ACCESSORIES_CAMPAIGN_LINES}
        cta="Explore Accessories"
        to="/accessories"
        image="/brand/monogram-hero.jpg"
        testId="accessories-campaign"
      />
      <div className="px-6 lg:px-16 max-w-[1500px] mx-auto py-28 lg:py-40 space-y-32 lg:space-y-48">
        {accessories.map((p, i) => (
          <EditorialPiece key={p.id} product={p} index={i + 3} flip={i % 2 === 0} />
        ))}
      </div>

      <Signature product={signature} />
      <Archive products={archive} />
      <BrandWorld />
      <PrivateAccess />
    </div>
  );
}
